import { useTranslation } from "react-i18next";
import {
  CashOutAmount,
  ESenderRole,
  IContractDetailsResponseData,
} from "../../../../setup/api/services/contract/models";
import styles from "./styles.module.scss";
import { useMemo } from "react";
import PartyTable from "../PartyTable";
import { EPARTY_STATUS } from "../../../../enums";
import AmountCard from "../AmountCard";
import { findPartyForProfile } from "../../../../setup/util/contract";
import {
  getTwoPartyCashout,
  isTwoPartyContract,
} from "../../../../setup/util/twoPartyContract";

interface CashoutDetailsProps {
  milestone: IContractDetailsResponseData | undefined;
}

const CashoutDetails = ({ milestone }: CashoutDetailsProps) => {
  const { t } = useTranslation();
  const accountId = milestone?.organization?.accountId;
  // Two-party transaction (WAF-3235): Waffy is the implicit broker, and its fee comes out of the
  // seller's (or refunded buyer's) gross `amountDue` — so a party's payout is net of it, and the
  // organization is the seller, not a broker.
  const isTwoParty = isTwoPartyContract(milestone);
  const netAmount = (cashOut: CashOutAmount) =>
    getTwoPartyCashout(milestone, cashOut)?.net ?? (cashOut?.amountDue || 0);

  const activeCashOuts = useMemo(
    () =>
      milestone?.cashOutAmountList?.filter((c) => (c.amountDue || 0) > 0) ?? [],
    [milestone?.cashOutAmountList]
  );

  const getLastTransaction = (
    cashOut: { userIdentification?: string; settlementRequestId?: string },
    profileId: number | undefined,
    types: string[]
  ) => {
    if (cashOut.settlementRequestId) {
      const settlementRequest = milestone?.settlementRequests?.find(
        (sr) => sr.id === cashOut.settlementRequestId
      );
      if (settlementRequest?.executionTransactionId) {
        return (
          milestone?.transactions.find(
            (t) =>
              t.id === settlementRequest.executionTransactionId ||
              t.referenceTransactionId ===
                settlementRequest.executionTransactionId
          ) ?? null
        );
      }
      return null;
    }
    const partyTransactions = milestone?.transactions.filter(
      (t) =>
        (t.beneficiaryId === String(profileId) ||
          t.beneficiaryId === cashOut.userIdentification) &&
        types.includes(t.type)
    );
    return partyTransactions?.length
      ? partyTransactions[partyTransactions.length - 1]
      : null;
  };

  const getProfileForCashOut = (userIdentification: string | undefined) => {
    if (!userIdentification) return undefined;
    return (
      milestone?.partiesProfiles?.CUSTOMER?.find(
        (p) => String(p.userIdentification) === userIdentification
      ) ??
      milestone?.partiesProfiles?.PROVIDER?.find(
        (p) => String(p.userIdentification) === userIdentification
      ) ??
      milestone?.partiesProfiles?.BROKER?.find(
        (p) => String(p.userIdentification) === userIdentification
      )
    );
  };

  const getRoleForCashOut = (userIdentification: string | undefined) => {
    if (!userIdentification) return null;
    if (
      milestone?.partiesProfiles?.CUSTOMER?.some(
        (p) => String(p.userIdentification) === userIdentification
      )
    )
      return "CUSTOMER";
    if (
      milestone?.partiesProfiles?.PROVIDER?.some(
        (p) => String(p.userIdentification) === userIdentification
      )
    )
      return "PROVIDER";
    if (
      milestone?.partiesProfiles?.BROKER?.some(
        (p) => String(p.userIdentification) === userIdentification
      )
    )
      return "BROKER";
    return null;
  };

  const customers = useMemo(
    () =>
      activeCashOuts.filter(
        (c) => getRoleForCashOut(c.userIdentification) === "CUSTOMER"
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeCashOuts, milestone?.partiesProfiles]
  );

  const providers = useMemo(
    () =>
      activeCashOuts.filter(
        (c) => getRoleForCashOut(c.userIdentification) === "PROVIDER"
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeCashOuts, milestone?.partiesProfiles]
  );

  const brokers = useMemo(
    () =>
      activeCashOuts.filter(
        (c) => getRoleForCashOut(c.userIdentification) === "BROKER"
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeCashOuts, milestone?.partiesProfiles]
  );

  const amountSettled = useMemo(() => {
    const admin = milestone?.cashOutAmountList?.find(
      (amount) => amount?.id === accountId
    );
    // Two-party: the seller's amountDue already includes Waffy's fee.
    if (isTwoParty) return admin?.amountDue ?? 0;
    return admin?.amountDue !== undefined
      ? admin?.amountDue + (milestone?.fee || 0)
      : 0;
  }, [milestone?.cashOutAmountList, milestone?.fee, accountId, isTwoParty]);

  const waffyFee = useMemo(() => {
    if (!isTwoParty) return milestone?.fee || 0;
    return (milestone?.cashOutAmountList || []).reduce(
      (sum, cashOut) =>
        sum + (getTwoPartyCashout(milestone, cashOut)?.fee || 0),
      0
    );
  }, [milestone, isTwoParty]);

  const profit = useMemo(() => {
    const admin = milestone?.cashOutAmountList?.find(
      (amount) => amount?.id === accountId
    );
    if (admin?.amountDue === undefined) return 0;
    return isTwoParty ? netAmount(admin) : admin?.amountDue;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [milestone, accountId, isTwoParty]);

  return (
    <>
      {!!customers?.length && (
        <div className={styles.partyContainer}>
          <h2>{t("lbl_payees")}</h2>

          <PartyTable
            rows={customers.map((cashOut) => {
              const profile = getProfileForCashOut(cashOut.userIdentification);
              const currentParty = findPartyForProfile(
                milestone?.parties,
                profile,
                ESenderRole.CUSTOMER
              );
              const partyLastTransaction = getLastTransaction(
                cashOut,
                profile?.id,
                ["CASH_OUT", "REFUND_CUSTOMER"]
              );
              return {
                id: profile?.id?.toString() || "",
                name: profile?.profile?.fullName || "",
                phoneNumber:
                  profile?.phoneNumber || currentParty?.phoneNumber || "",
                amount: netAmount(cashOut),
                status:
                  currentParty?.status === EPARTY_STATUS.REFUNDED ||
                  currentParty?.status === EPARTY_STATUS.RELEASED
                    ? currentParty.status
                    : cashOut.settlementAmountStatus === "TRANSFERRED"
                    ? EPARTY_STATUS.RELEASED
                    : cashOut.settlementAmountStatus ===
                      String(EPARTY_STATUS.AVAILABLE_BALANCE)
                    ? EPARTY_STATUS.AVAILABLE_BALANCE
                    : EPARTY_STATUS.WAITING_FOR_RELEASE,
                type: "customer",
                image: profile?.profile?.photoUrl || "",
                cashoutTime: partyLastTransaction
                  ? partyLastTransaction.createdAt
                  : undefined,
                referenceNumber: partyLastTransaction
                  ? partyLastTransaction.id
                  : undefined,
              };
            })}
            columns={{
              showStatus: true,
              showCashoutTime: true,
              showReferenceNumber: true,
            }}
          />
        </div>
      )}

      {!!providers?.length && (
        <div className={styles.partyContainer}>
          <h2>{t("lbl_receivers")}</h2>

          <PartyTable
            rows={providers.map((cashOut) => {
              const profile = getProfileForCashOut(cashOut.userIdentification);
              const currentParty = findPartyForProfile(
                milestone?.parties,
                profile,
                ESenderRole.PROVIDER
              );
              const partyLastTransaction = getLastTransaction(
                cashOut,
                profile?.id,
                ["CASH_OUT", "RELEASE_TO_PROVIDER", "CLOSE_RELEASE_TO_PROVIDER"]
              );
              return {
                id: profile?.id?.toString() || "",
                name: profile?.profile?.fullName || "",
                phoneNumber:
                  profile?.phoneNumber || currentParty?.phoneNumber || "",
                amount: netAmount(cashOut),
                status:
                  currentParty?.status === EPARTY_STATUS.RELEASED
                    ? currentParty.status
                    : cashOut.settlementAmountStatus ===
                      String(EPARTY_STATUS.AVAILABLE_BALANCE)
                    ? EPARTY_STATUS.AVAILABLE_BALANCE
                    : EPARTY_STATUS.WAITING_FOR_RELEASE,
                type: "provider",
                image: profile?.profile?.photoUrl || "",
                cashoutTime: partyLastTransaction
                  ? partyLastTransaction.createdAt
                  : undefined,
                referenceNumber: partyLastTransaction
                  ? partyLastTransaction.id
                  : undefined,
              };
            })}
            columns={{
              showStatus: true,
              showCashoutTime: true,
              showReferenceNumber: true,
            }}
          />
        </div>
      )}

      {!!brokers?.length && (
        <div className={styles.partyContainer}>
          <h2>{t("lbl_brokers")}</h2>

          <PartyTable
            rows={brokers.map((cashOut) => {
              const profile = getProfileForCashOut(cashOut.userIdentification);
              const currentParty = findPartyForProfile(
                milestone?.parties,
                profile,
                ESenderRole.BROKER
              );
              const partyLastTransaction = getLastTransaction(
                cashOut,
                profile?.id,
                ["CASH_OUT", "RELEASE_TO_PROVIDER", "CLOSE_RELEASE_TO_PROVIDER"]
              );
              return {
                id: profile?.id?.toString() || "",
                name: profile?.profile?.fullName || "",
                phoneNumber:
                  profile?.phoneNumber || currentParty?.phoneNumber || "",
                amount: cashOut?.amountDue || 0,
                status:
                  currentParty?.status === EPARTY_STATUS.RELEASED
                    ? currentParty.status
                    : cashOut.settlementAmountStatus ===
                      String(EPARTY_STATUS.AVAILABLE_BALANCE)
                    ? EPARTY_STATUS.AVAILABLE_BALANCE
                    : EPARTY_STATUS.WAITING_FOR_RELEASE,
                type: "provider",
                image: profile?.profile?.photoUrl || "",
                cashoutTime: partyLastTransaction
                  ? partyLastTransaction.createdAt
                  : undefined,
                referenceNumber: partyLastTransaction
                  ? partyLastTransaction.id
                  : undefined,
              };
            })}
            columns={{
              showStatus: true,
              showCashoutTime: true,
              showReferenceNumber: true,
            }}
          />
        </div>
      )}

      <div className={styles.cardsContainer}>
        <AmountCard
          title={t("lbl_value_assigned_to_you")}
          amount={String(amountSettled?.toFixed(2))}
          style={{ backgroundColor: "#F8F9FB" }}
          isSmall
        />
        <AmountCard
          title={t("lbl_waffy_fees_amount")}
          amount={String(waffyFee.toFixed(2))}
          style={{ backgroundColor: "#F8F9FB" }}
          isSmall
        />
        <AmountCard
          title={t("lbl_your_profit")}
          amount={String(profit?.toFixed(2))}
          style={{
            backgroundColor: profit === 0 ? "#F8F9FB" : undefined,
          }}
          specialStyle={
            profit > 0 ? "positive" : profit < 0 ? "negative" : undefined
          }
          isSmall
        />
      </div>
    </>
  );
};

export default CashoutDetails;
